import PropTypes from 'prop-types';
import React, { CSSProperties, useMemo } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import ErrorComponent from '../../atoms/error/error';
import Card from '../../molecules/card/card';
import DataCenterSkeleton from '../../molecules/data-center-skeleton/data-center-skeleton';
import ErrorBoundary from '../error-boundary/error-boundary';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import './card-motorsports-teams-ranking.scss';
import { ComposableList } from '../../organisms';
import { IComposableListAccessibleItem } from '../../organisms/composable-list/composable-list';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { ImgPlaceholder } from '../../atoms';
import { DRIVERFLAG, placeholdersMap } from '../../../utils/placeholders-motorsports';

export interface ITeam extends IComposableListAccessibleItem {
  logoUrl?: string | null;
  points?: string | null;
  position: string;
  teamName: string;
  teamUrl?: string | null;
}

export interface ICardMotorsportsTeamsRanking extends IComponent {
  competitionName?: string;
  readMoreLink?: string;
  teams: Array<ITeam>;
  isLoading?: boolean;
  cardCtaTrackingData?: {};
}

const isValidTeamssList = (teams: ITeam[], isLoading: boolean) => isLoading || Boolean(teams && teams.length);

const classNames = (): IClassNames =>
  makeClassNames({
    cardRanking: 'card-teams-ranking',
    cardRankingTeamsWrapper: 'card-teams-ranking__teams-wrapper',
  });

const CardMotorsportsTeamsRanking: React.FC<ICardMotorsportsTeamsRanking> = ({
  competitionName,
  readMoreLink,
  teams,
  isLoading = false,
  trackingData = {},
  cardCtaTrackingData = {},
}) => {
  const cn = classNames();
  const hasError = useMemo<boolean>(() => !isValidTeamssList(teams, isLoading), [teams, isLoading]);

  return (
    <aside
      className={cn.cardRanking}
      {...objectToDataAttributes(trackingData)}
      role="document"
      style={{ '--cl-header-height': 'auto' } as CSSProperties}
    >
      <ErrorBoundary hasError={hasError} errorComponent={<ErrorComponent>{t('unavailable.data')}</ErrorComponent>}>
        {isLoading ? (
          <DataCenterSkeleton size="small" rowCount={5} />
        ) : (
          <Card
            cta={t('card.view.all')}
            ctaLink={readMoreLink}
            title={`${t('ranking.cta')} ${competitionName}`}
            size="small"
            ctaMobile
            ctaMobileWhite
            trackingData={cardCtaTrackingData}
          >
            <ComposableList<ITeam>
              headers={{
                position: 'Pos',
                teamName: 'Team',
                points: 'P',
              }}
              items={teams}
              fields={[
                'position',
                {
                  prop: 'teamName',
                  render: (data) => (
                    <span className={cn.cardRankingTeamsWrapper}>
                      <ImgPlaceholder
                        placeholder={placeholdersMap[DRIVERFLAG]}
                        loading="lazy"
                        title=""
                        src={data.logoUrl ?? undefined}
                        aria-hidden
                      />{' '}
                      <span>{data.teamName}</span>
                    </span>
                  ),
                },
                'points',
              ]}
              inverted
              aria-label="classifica"
              aria-description={`Classifica ${competitionName}`}
            />
          </Card>
        )}
      </ErrorBoundary>
    </aside>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CardMotorsportsTeamsRanking.propTypes = {
    competitionName: PropTypes.string,
    isLoading: PropTypes.bool,
    readMoreLink: PropTypes.string,
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        logoUrl: PropTypes.string,
        points: PropTypes.string,
        position: PropTypes.string.isRequired,
        teamName: PropTypes.string.isRequired,
        teamUrl: PropTypes.string,
      }).isRequired
    ).isRequired,
  };
}

export default CardMotorsportsTeamsRanking;
